import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CurrencyModel, MonetaryValueTypeModel } from '@railmybox/api-pricing';
import { AbstractValueAccessorComponent, FieldType, ValueAccessorUtil } from '@railmybox/shared/util';
import { Observable, tap } from 'rxjs';
import { ValueCurrencyService } from './service/valueCurrency.service';

@UntilDestroy()
@Component({
  selector: 'rmb-value-currency',
  templateUrl: './value-currency.component.html',
  styleUrl: './value-currency.component.scss',
  providers: [...ValueAccessorUtil.getValueAccessorProvider(ValueCurrencyComponent)],
  standalone: false,
})
export class ValueCurrencyComponent extends AbstractValueAccessorComponent<MonetaryValueTypeModel | null> implements OnInit, OnChanges {
  private formBuilder = inject(FormBuilder);
  private valueCurrencyService = inject(ValueCurrencyService);

  @Input() defaultCurrency = 'EUR';
  @Input() currencyList$: Observable<CurrencyModel[]>;
  @Output() euroLimitExceededChange = new EventEmitter<boolean>();

  @Input() set formRules(rule: 'M' | 'O' | 'R') {
    this.formRule = rule;
    switch (rule) {
      case 'O':
        this.setFormValidators(null);
        break;
      case 'M':
        this.setFormValidators(Validators.required);
        break;
      case 'R':
        this.valueCurrencyForm.disable();
    }
  }
  currencyList: CurrencyModel[] = [];
  valueCurrencyForm: FormGroup;
  formRule: FieldType | 'R';
  tooltip: string;

  async ngOnInit(): Promise<void> {
    this.valueCurrencyForm = this.getCurrencyForm();

    this.currencyList$ = this.valueCurrencyService.getCurrencyList();
    this.currencyList$.pipe(untilDestroyed(this)).subscribe((list) => {
      this.currencyList = list;
      this.setFormValidators(null);
    });

    this.valueCurrencyForm
      .get('currency')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.valueCurrencyForm.get('amount').updateValueAndValidity();
          this.getTooltip();
        },
      });
    this.onCurrencyChange();
    this.setFormValidators(null);
  }

  updateFormValidity(): void {
    this.valueCurrencyForm.markAllAsTouched();
    this.onTouched();
    this.valueCurrencyForm.patchValue(this.valueCurrencyForm.getRawValue());
  }

  writeValue(value: MonetaryValueTypeModel) {
    super.writeValue(value);
    if (value?.amount) {
      this.valueCurrencyForm.patchValue(value);
    }
  }

  ngOnChanges(simpleChanges: SimpleChanges): void {
    super.ngOnChanges(simpleChanges);
    if (this.defaultCurrency) {
      this.valueCurrencyForm?.patchValue({ currency: this.defaultCurrency });
    }

    if (this._fieldType === 'M') {
      this.valueCurrencyForm?.markAllAsTouched();
      this.setFormValidators(Validators.required);
    }
  }

  onCurrencyChange(): void {
    this.valueCurrencyForm.valueChanges.pipe(untilDestroyed(this)).subscribe((currencyValue) => {
      if (!currencyValue?.amount) {
        currencyValue = null;
      }
      this.updateEuroLimitExceeded();

      if (this.valueCurrencyForm.invalid) {
        return;
      }

      this.onChange(currencyValue);
      this.changed.emit(currencyValue);
    });
  }

  updateEuroLimitExceeded(): void {
    const amount = this.valueCurrencyForm?.get('amount')?.value;
    const currency = this.valueCurrencyForm?.get('currency')?.value;
    const matchedCurrency = this.currencyList.find((c) => c.alphaCode === currency);

    let exceeded = false;
    if (matchedCurrency && amount) {
      const euroValue = amount / matchedCurrency.euroConversionFactor;
      exceeded = euroValue > 500000;
    }

    this.euroLimitExceededChange.emit(exceeded);
  }

  getTooltip() {
    this.currencyList$
      .pipe(
        tap((currencyArray) => {
          const matchedCurrency = currencyArray.find((item) => this.valueCurrencyForm.get('currency').value === item.alphaCode);
          if (matchedCurrency) {
            this.tooltip = matchedCurrency.name;
          }
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  onBlur(): void {
    this.valueCurrencyForm.markAllAsTouched();
    this.valueCurrencyForm.updateValueAndValidity();
    this.onTouched();
  }

  getCurrencyForm(): FormGroup {
    return this.formBuilder.group({
      amount: [null],
      currency: [this.defaultCurrency],
    });
  }

  private setFormValidators(validatorFn: ValidatorFn | null): void {
    const valueCurrencyValidators: ValidatorFn[] = [Validators.min(1)];

    if (validatorFn) {
      valueCurrencyValidators.unshift(validatorFn);
    }

    this.valueCurrencyForm?.get('amount').setValidators(valueCurrencyValidators);
    this.valueCurrencyForm?.get('currency').setValidators(validatorFn);
    this.valueCurrencyForm?.updateValueAndValidity();
  }

  setDisabledState(isDisabled: boolean) {
    super.setDisabledState(isDisabled);
    const disableFn = isDisabled ? 'disable' : 'enable';
    this.valueCurrencyForm[disableFn]({ emitEvent: false });
  }
}
